import { AxiosError } from "axios";
import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { OrgUserType, UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";

type FormValues = {
  email: string;
  first_name: string;
  last_name: string;
  external_user_id: string;
};

interface EditOrgUserFormProps {
  className?: string;
  onClose: () => void;
  user: UserType;
  orgUser: OrgUserType;
}

export const EditOrgUserForm: FC<EditOrgUserFormProps> = ({
  className,
  orgUser,
  user,
  onClose,
}) => {
  const form = useForm<FormValues>({
    defaultValues: {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      external_user_id: orgUser.external_user_id || "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    trackEvent("Edit org user - Save changes", {
      ...BUTTON_EVENT_CATEGORY,
      userEmail: formData.email,
      oldUserEmail: user.email,
    });

    const userPromise = storeApi.users
      .partial_update(
        {
          id: user.id,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
        },
        { skipToast: true },
      )
      .catch((err) => {
        if (err instanceof AxiosError) {
          const data = err.response?.data;
          if (data && typeof data === "object") {
            const emailErrMsg = Array.isArray(data.email) ? data.email[0] : data.email;
            if (typeof emailErrMsg === "string") {
              // If the error message is about email uniqueness, set the form error
              if (emailErrMsg === "err_email_already_exists") {
                form.setError("email", {
                  type: "server",
                  message: t("error.unique_email"),
                });
              } else if (emailErrMsg === "Enter a valid email address.") {
                form.setError("email", {
                  type: "server",
                  message: t("error.invalid_email"),
                });
              }
              // re-throw so that the form doesn't close
              throw err;
            }
          }
        }
        // if we reach this point, we should show the toast error
        toastLocalizedOperationCatcher("update_failure", {
          item: t("glossary.users"),
        })(err);
        throw err;
      });

    const orgUserPromise = storeApi.org_users
      .partial_update(
        {
          id: orgUser.id,
          // If external_user_id is the empty string, we send null to the API
          external_user_id: formData.external_user_id || null,
        },
        { skipToast: true },
      )
      .catch((err) => {
        if (err instanceof AxiosError) {
          const data = err.response?.data;
          if (data && typeof data === "object") {
            const externalIdErrMsg = Array.isArray(data.external_user_id)
              ? data.external_user_id[0]
              : data.external_user_id;
            if (typeof externalIdErrMsg === "string") {
              form.setError("external_user_id", {
                type: "server",
                message: t("error.unique_external_id"),
              });
              // re-throw so that the form doesn't close
              throw err;
            }
          }
        }
        // if we reach this point, we should show the toast error
        toastLocalizedOperationCatcher("update_failure", {
          item: t("glossary.org_users"),
        })(err);
        throw err;
      });

    // If both promises resolve, we show a success message and close the form
    const combinedPromise = Promise.all([userPromise, orgUserPromise]);
    return (
      combinedPromise
        .then(() => {
          toastSuccessMessage(t("success.toasts.updated_user_info"));
          onClose();
        })
        // Catch unhandled errors -- no need to do anything with them
        .catch(() => {})
    );
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge("flex flex-col gap-3", className)}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {(
          [
            ["email", true],
            ["first_name", true],
            ["last_name", false],
            ["external_user_id", false],
          ] as const
        ).map(([field, required]) => (
          <FormField
            key={field}
            label={t(`user_options_menu.edit_user_info.${field}`)}
            name={field}
            required={required}
          >
            {(controlProps) => (
              <TextField
                {...form.register(field, {
                  required: required ? t("error.field_required") : false,
                })}
                {...controlProps}
              />
            )}
          </FormField>
        ))}

        <SubmitOrCancelButtons className="pt-8" onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
